import { Link } from "@tanstack/react-router";
import { cx } from "@fp/ui/utils/cx";
import { HelpCircle, LayersThree01, X } from "@untitledui/icons";
import { useEffect, useRef, useState } from "react";
import {
  Button as AriaButton,
  Dialog,
  DialogTrigger,
  Heading,
  Popover,
} from "react-aria-components";
import { useHotkeys } from "react-hotkeys-hook";
import { BatchProcessCard } from "./batch-process-card";
import { useBatchProcesses } from "./batch-process-context";
import { KeyHint } from "@/components/key-hint";
import { isOverlayOpen } from "@/lib/is-overlay-open";

const RECENT_LIMIT = 5;

const SHORTCUTS: { label: string; keys: string[] }[] = [
  { label: "Open/close drawer", keys: ["~"] },
  { label: "Show/hide shortcuts", keys: ["?"] },
];

export function BatchProcessDrawer() {
  const { batches, canView } = useBatchProcesses();
  const [isOpen, setIsOpen] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const tabRef = useRef<HTMLButtonElement>(null);

  useHotkeys(
    "backquote, shift+backquote",
    () => {
      if (isOpen) {
        tabRef.current?.blur();
        setIsOpen(false);
        return;
      }
      setIsOpen(!isOverlayOpen());
    },
    { enabled: canView },
    [canView, isOpen],
  );

  useHotkeys(
    "shift+slash",
    () => {
      setIsHelpOpen((open) => !open);
    },
    { enabled: canView && isOpen },
    [canView, isOpen],
  );

  useEffect(() => {
    if (!isOpen) {
      setIsHelpOpen(false);
    }
  }, [isOpen]);

  if (!canView) {
    return null;
  }

  const recentBatches = batches.slice(0, RECENT_LIMIT);
  const hasActiveBatch = batches.some((batch) => !batch.isComplete);

  return (
    <div className="pointer-events-none fixed inset-0 z-50 overflow-hidden">
      <button
        type="button"
        aria-label="Close batch processes"
        tabIndex={isOpen ? 0 : -1}
        onClick={() => setIsOpen(false)}
        className={cx(
          "absolute inset-0 bg-black/20 transition-opacity duration-200",
          isOpen ? "pointer-events-auto opacity-100" : "opacity-0",
        )}
      />

      <div
        className={cx(
          "pointer-events-auto absolute right-0 top-0 h-full w-[min(26rem,100vw)] transition-transform duration-200 ease-out",
          isOpen ? "translate-x-0" : "translate-x-full",
        )}
      >
        <button
          ref={tabRef}
          type="button"
          onClick={() => setIsOpen((open) => !open)}
          aria-label={isOpen ? "Close batch processes" : "Open batch processes"}
          aria-expanded={isOpen}
          className="absolute right-full top-1/2 flex -translate-y-1/2 items-center gap-1.5 rounded-l-lg border border-r-0 border-secondary bg-primary px-2 py-3 shadow-md outline-focus-ring hover:bg-primary_hover focus-visible:outline-2 focus-visible:outline-offset-2"
        >
          <LayersThree01 className="size-4 text-fg-quaternary" />
          {hasActiveBatch && (
            <span className="relative flex size-2">
              <span className="absolute inline-flex size-full animate-ping rounded-full bg-fg-brand-primary opacity-75" />
              <span className="relative inline-flex size-2 rounded-full bg-fg-brand-primary" />
            </span>
          )}
        </button>

        <div
          role={isOpen ? "dialog" : undefined}
          aria-label="Batch processes"
          aria-hidden={!isOpen}
          className="flex h-full flex-col bg-secondary_alt shadow-xl"
        >
          <div className="flex items-center justify-between border-b border-secondary px-4 py-3">
            <div className="flex items-center gap-2">
              <LayersThree01 className="size-4 text-fg-quaternary" />
              <span className="text-sm font-semibold text-primary">
                Last {RECENT_LIMIT} batch processes
              </span>
            </div>
            <div className="flex items-center gap-1">
              <Link
                to="/batch-processes"
                onClick={() => setIsOpen(false)}
                className="mr-1 text-sm font-medium text-brand-secondary hover:text-brand-secondary_hover"
              >
                View all
              </Link>
              <DialogTrigger isOpen={isHelpOpen} onOpenChange={setIsHelpOpen}>
                <AriaButton
                  aria-label="Keyboard shortcuts"
                  aria-keyshortcuts="?"
                  className="rounded-md p-1 text-fg-quaternary outline-focus-ring hover:bg-primary_hover hover:text-fg-quaternary_hover focus-visible:outline-2 focus-visible:outline-offset-2"
                >
                  <HelpCircle className="size-4" />
                </AriaButton>
                <Popover
                  placement="bottom end"
                  className="rounded-xl border border-secondary bg-primary p-4 shadow-lg outline-none"
                >
                  <Dialog className="outline-none">
                    <Heading
                      slot="title"
                      level={2}
                      className="mb-3 text-sm font-semibold text-primary"
                    >
                      Keyboard shortcuts
                    </Heading>
                    <dl className="space-y-2">
                      {SHORTCUTS.map((shortcut) => (
                        <div
                          key={shortcut.label}
                          className="flex items-center justify-between gap-8 text-sm text-secondary"
                        >
                          <dt>{shortcut.label}</dt>
                          <dd className="flex gap-1">
                            {shortcut.keys.map((key) => (
                              <KeyHint key={key}>{key}</KeyHint>
                            ))}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  </Dialog>
                </Popover>
              </DialogTrigger>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                aria-label="Close"
                className="rounded-md p-1 text-fg-quaternary outline-focus-ring hover:bg-primary_hover hover:text-fg-quaternary_hover focus-visible:outline-2 focus-visible:outline-offset-2"
              >
                <X className="size-4" />
              </button>
            </div>
          </div>

          <div className="flex flex-1 flex-col gap-2 overflow-y-auto p-3">
            {recentBatches.length === 0 ? (
              <p className="px-1 py-4 text-sm text-tertiary">
                No batch processes yet.
              </p>
            ) : (
              recentBatches.map((batch) => (
                <BatchProcessCard
                  key={batch.id}
                  batch={batch}
                  isExpanded={expandedId === batch.id}
                  onToggleExpand={() =>
                    setExpandedId((id) => (id === batch.id ? null : batch.id))
                  }
                />
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
